import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ArrowLeft, Heart } from "lucide-react";
import { ASSETS } from "../lib/assets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null, error: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: null, error: "Missing session_id" });
      return;
    }

    let timer;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        const data = res.data;
        if (data.payment_status === "paid") {
          setState({ status: "paid", data, error: null });
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState({ status: "failed", data, error: null });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "timeout", data, error: null });
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        setState({ status: "error", data: null, error: err?.response?.data?.detail || err.message });
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-black relative overflow-hidden flex items-center justify-center p-6">
      <div className="absolute inset-0 opacity-20">
        <img src={ASSETS.heroMedals} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
      </div>

      <div className="relative max-w-xl w-full bg-[#111111] border border-white/10 p-10 md:p-14 text-center">
        {state.status === "polling" && (
          <>
            <Loader2 size={56} className="text-[#C4F542] mx-auto mb-6 animate-spin" />
            <h1 className="font-display text-4xl md:text-5xl text-white mb-3">CONFIRMING...</h1>
            <p className="text-zinc-400 font-body">Hold tight — verifying your donation with Stripe.</p>
          </>
        )}

        {state.status === "paid" && (
          <>
            <CheckCircle2 size={64} className="text-[#C4F542] mx-auto mb-6" data-testid="payment-success-icon" />
            <p className="text-xs uppercase tracking-[0.4em] text-[#C4F542] mb-4">Donation received</p>
            <h1 className="font-display text-5xl md:text-6xl text-white mb-4">
              THANK <span className="text-[#C4F542]">YOU.</span>
            </h1>
            {state.data?.amount ? (
              <p className="text-lg text-zinc-300 font-body">
                Your gift of <span className="text-[#C4F542] font-bold">${(state.data.amount / 100).toFixed(2)}</span> just built a champion.
              </p>
            ) : (
              <p className="text-lg text-zinc-300 font-body">Your donation just built a champion.</p>
            )}
            <p className="mt-4 text-sm text-zinc-500 font-body">
              A receipt is on its way to your inbox from Stripe.
            </p>
            <Link
              to="/"
              data-testid="payment-back-home"
              className="mt-10 inline-flex items-center justify-center gap-3 bg-[#C4F542] text-black font-bold uppercase tracking-widest text-sm py-4 px-8 hover:bg-white transition-colors"
            >
              <ArrowLeft size={16} /> Back to site
            </Link>
          </>
        )}

        {["failed", "timeout", "error"].includes(state.status) && (
          <>
            <XCircle size={56} className="text-red-400 mx-auto mb-6" />
            <h1 className="font-display text-4xl md:text-5xl text-white mb-3">
              {state.status === "timeout" ? "STILL PROCESSING" : "SOMETHING WENT WRONG"}
            </h1>
            <p className="text-zinc-400 font-body">
              {state.status === "timeout"
                ? "Your payment is still being processed. You'll receive a confirmation email from Stripe once it clears."
                : state.error || "We couldn't confirm your donation. If you were charged, please contact us."}
            </p>
            <Link
              to="/"
              className="mt-10 inline-flex items-center justify-center gap-3 border border-white/30 text-white font-bold uppercase tracking-widest text-sm py-4 px-8 hover:border-[#C4F542] hover:text-[#C4F542] transition-colors"
            >
              <ArrowLeft size={16} /> Back to site
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export const PaymentCancel = () => (
  <div className="min-h-screen bg-black relative overflow-hidden flex items-center justify-center p-6">
    <div className="absolute inset-0 opacity-20">
      <img src={ASSETS.drills} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
    </div>
    <div className="relative max-w-xl w-full bg-[#111111] border border-white/10 p-10 md:p-14 text-center">
      <Heart size={56} className="text-zinc-500 mx-auto mb-6" />
      <p className="text-xs uppercase tracking-[0.4em] text-zinc-400 mb-4">No charge made</p>
      <h1 className="font-display text-5xl md:text-6xl text-white mb-4">
        MAYBE <span className="text-[#C4F542]">NEXT TIME.</span>
      </h1>
      <p className="text-lg text-zinc-300 font-body">
        Your donation wasn't completed. Whenever you're ready, we're here — and so are the kids.
      </p>
      <Link
        to="/#donate"
        data-testid="payment-cancel-retry"
        className="mt-10 inline-flex items-center justify-center gap-3 bg-[#C4F542] text-black font-bold uppercase tracking-widest text-sm py-4 px-8 hover:bg-white transition-colors"
      >
        Try Again
      </Link>
    </div>
  </div>
);
